/**
 * AdvancedDonationModal.tsx - Advanced Donation Interface Modal
 *
 * This component provides an advanced donation interface with donation item
 * selection, quantity management, and enhanced donation options beyond the
 * standard donation form.
 *
 * Key Features:
 * - Donation item selection with quantities
 * - Advanced donation options and customization
 * - Multi-step donation flow within modal
 * - Donation item preview and details
 * - Quantity adjustment and pricing calculation
 * - Integration with donation capture flow
 *
 * Donation Items:
 * - Item selection with images and descriptions
 * - Quantity controls with min/max validation
 * - Price calculation and total display
 * - Custom donation amount options
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - isPreview?: boolean - Whether in preview mode
 *
 * Dependencies: MobX store, styled-components, donation capture integration
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { X, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { Toast } from "./Toast";
import { FundRaiserStatus, MediaType } from "../enums";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MediaSection = styled.div`
  width: 100%;
  height: 300px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-radius: 24px 24px 0 0;
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MediaVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MediaPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 2px;
`;

const ContentSection = styled.div`
  padding: 32px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin-bottom: 32px;
  text-align: center;
  line-height: 1.5;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  text-align: center;
`;

const SectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const AmountSection = styled.div`
  margin-bottom: 32px;
`;

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const AmountButton = styled.button<{ selected: boolean }>`
  padding: 16px;
  border: 2px solid ${(props) => (props.selected ? "#4b5563" : "#d1d5db")};
  background: ${(props) => (props.selected ? "#4b5563" : "#ffffff")};
  color: ${(props) => (props.selected ? "#ffffff" : "#4b5563")};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4b5563;
    background: ${(props) => (props.selected ? "#4b5563" : "#f9fafb")};
  }
`;

const CustomAmountInput = styled.input`
  padding: 16px;
  padding-right: 16px;
  border: 2px solid #d1d5db;
  background: #ffffff;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 100%;
  min-height: 56px;
  box-sizing: border-box;
  text-align: center;

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }

  &:focus {
    outline: none;
    border-color: #4b5563;
  }

  &:hover {
    border-color: #4b5563;
    background: #f9fafb;
  }
`;

const IncentiveText = styled.div`
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: #4b5563;
  text-align: center;
  margin-bottom: 24px;
`;

const FrequencySection = styled.div`
  margin-bottom: 32px;
`;

const FrequencyToggle = styled.div`
  display: flex;
  background: #f9fafb;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 12px;
`;

const FrequencyButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.selected ? "#4b5563" : "transparent")};
  color: ${(props) => (props.selected ? "#ffffff" : "#6b7280")};

  &:hover {
    color: ${(props) => (props.selected ? "#ffffff" : "#4b5563")};
  }
`;

const FrequencyHelpText = styled.div`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  font-style: italic;
`;

const BenefitSection = styled.div`
  margin-bottom: 32px;
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const BenefitButton = styled.button<{ selected: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.selected ? "#4b5563" : "#d1d5db")};
  background: ${(props) => (props.selected ? "#4b5563" : "#ffffff")};
  color: ${(props) => (props.selected ? "#ffffff" : "#4b5563")};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4b5563;
    background: ${(props) => (props.selected ? "#4b5563" : "#f9fafb")};
  }
`;

const CheckboxSection = styled.div`
  margin-bottom: 24px;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
`;

const DedicationInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 8px;

  &:focus {
    outline: none;
    border-color: #4b5563;
  }
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  margin-top: 8px;

  &:focus {
    outline: none;
    border-color: #4b5563;
  }
`;

const PercentageGrid = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const PercentageButton = styled.button<{ selected: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${(props) => (props.selected ? "#4b5563" : "#d1d5db")};
  background: ${(props) => (props.selected ? "#4b5563" : "#ffffff")};
  color: ${(props) => (props.selected ? "#ffffff" : "#4b5563")};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4b5563;
  }
`;

const DonateButton = styled.button`
  width: 100%;
  padding: 18px;
  background: #4b5563;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    background: #374151;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FooterText = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  line-height: 1.4;
`;

const presetAmounts = [25, 75, 150, 250, 500, 1000];
const benefitOptions = [
  "Business School",
  "Football Team",
  "Theater Department",
  "Student Services",
];
const percentageOptions = [3, 5, 10, 15, 20];

interface AdvancedDonationModalProps {
  isVisible: boolean;
  onClose: () => void;
  isPreview?: boolean;
}

export const AdvancedDonationModal: React.FC<AdvancedDonationModalProps> =
  observer(({ isVisible, onClose, isPreview = false }) => {
    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [customAmount, setCustomAmount] = useState("");
    const [isMonthly, setIsMonthly] = useState(false);
    const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
    const [showDedication, setShowDedication] = useState(false);
    const [dedicationText, setDedicationText] = useState("");
    const [comments, setComments] = useState("");
    const [showIncreases, setShowIncreases] = useState(false);
    const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
      null
    );
    const [coverProcessing, setCoverProcessing] = useState(false);

    const { builderData } = fundraiserStore;
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const isDraft =
      !isPreview && selectedFundraiser?.status === FundRaiserStatus.Draft;
    const mediaItems = builderData.mediaItems || [];
    const currentMedia = mediaItems.length > 0 ? mediaItems[0] : null;

    // Get title and subtitle from fundraiser data
    const modalTitle = builderData.title || "Make a Difference Today";
    const modalSubtitle =
      builderData.subtitle ||
      "Help make a difference in the lives of children. Donate generously today!";

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        if (isDraft) {
          ToastService.showSuccess(
            "Fundraiser is not active and cannot process a donation"
          );
          return;
        }
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    React.useEffect(() => {
      if (isVisible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }, [isVisible]);

    const handleAmountSelect = (amount: number) => {
      setSelectedAmount(amount);
      setCustomAmount("");
    };

    const handleCustomAmountChange = (value: string) => {
      setCustomAmount(value);
      setSelectedAmount(null);
    };

    const toggleBenefit = (benefit: string) => {
      setSelectedBenefits((prev) =>
        prev.includes(benefit)
          ? prev.filter((b) => b !== benefit)
          : [...prev, benefit]
      );
    };

    const getCurrentAmount = () => {
      if (customAmount) {
        return parseFloat(customAmount) || 0;
      }
      return selectedAmount || 0;
    };

    const getVideoEmbedUrl = (url: string) => {
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    };

    const handleDonateClick = () => {
      if (isDraft) {
        ToastService.showSuccess(
          "Fundraiser is not active and cannot process a donation"
        );
        return;
      }

      // Process donation logic here
      onClose();
    };

    const renderMedia = () => {
      if (!currentMedia) {
        return <MediaPlaceholder>MEDIA</MediaPlaceholder>;
      }

      if (currentMedia.type === MediaType.Image) {
        return (
          <MediaImage
            src={currentMedia.fullURL}
            alt={currentMedia.title || "Media"}
          />
        );
      } else {
        return (
          <MediaVideo
            src={getVideoEmbedUrl(currentMedia?.fullURL || "")}
            title={currentMedia.title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
    };

    return (
      <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>

          <MediaSection>{renderMedia()}</MediaSection>

          <ContentSection>
            <Title>{modalTitle}</Title>
            <Subtitle>{modalSubtitle}</Subtitle>

            <AmountSection>
              <SectionTitleContainer>
                <SectionTitle>Choose an Amount</SectionTitle>
              </SectionTitleContainer>

              <AmountGrid>
                {presetAmounts.map((amount) => (
                  <AmountButton
                    key={amount}
                    selected={selectedAmount === amount}
                    onClick={() => handleAmountSelect(amount)}
                  >
                    ${amount.toLocaleString()}
                  </AmountButton>
                ))}
              </AmountGrid>

              <IncentiveText>
                Donate $250.00 or more and receive a complementary gym bag as
                our thanks!
              </IncentiveText>
            </AmountSection>

            <FrequencySection>
              <SectionTitle>Make it Monthly</SectionTitle>
              <FrequencyToggle>
                <FrequencyButton
                  selected={!isMonthly}
                  onClick={() => setIsMonthly(false)}
                >
                  One Time
                </FrequencyButton>
                <FrequencyButton
                  selected={isMonthly}
                  onClick={() => setIsMonthly(true)}
                >
                  Monthly
                </FrequencyButton>
              </FrequencyToggle>
              <FrequencyHelpText>
                Help us out with a monthly donation!
              </FrequencyHelpText>
            </FrequencySection>

            <BenefitSection>
              <SectionTitle>Your Donation Should Benefit</SectionTitle>
              <BenefitGrid>
                {benefitOptions.map((benefit) => (
                  <BenefitButton
                    key={benefit}
                    selected={selectedBenefits.includes(benefit)}
                    onClick={() => toggleBenefit(benefit)}
                  >
                    {benefit}
                  </BenefitButton>
                ))}
              </BenefitGrid>
            </BenefitSection>

            <CheckboxSection>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={showDedication}
                  onChange={(e) => setShowDedication(e.target.checked)}
                />
                Yes, I want to dedicate my donation in honor or memory of
                someone.
              </CheckboxContainer>
              {showDedication && (
                <>
                  <DedicationInput
                    type="text"
                    placeholder="Dedicate to..."
                    value={dedicationText}
                    onChange={(e) => setDedicationText(e.target.value)}
                  />
                  <CommentTextArea
                    placeholder="Comments..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </>
              )}
            </CheckboxSection>

            <CheckboxSection>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={showIncreases}
                  onChange={(e) => setShowIncreases(e.target.checked)}
                />
                Yes, I want to help cover annual increases in expenses. Increase
                my monthly donation every year by...
              </CheckboxContainer>
              {showIncreases && (
                <PercentageGrid>
                  {percentageOptions.map((percent) => (
                    <PercentageButton
                      key={percent}
                      selected={selectedPercentage === percent}
                      onClick={() => setSelectedPercentage(percent)}
                    >
                      {percent}%
                    </PercentageButton>
                  ))}
                </PercentageGrid>
              )}
            </CheckboxSection>

            <CheckboxSection>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={coverProcessing}
                  onChange={(e) => setCoverProcessing(e.target.checked)}
                />
                Yes, I want to cover the cost to process my donation and put
                more of my money to work.
              </CheckboxContainer>
            </CheckboxSection>

            <DonateButton onClick={handleDonateClick} disabled={isDraft}>
              {isDraft
                ? "Fundraiser Not Active"
                : `Donate $${getCurrentAmount().toFixed(2)} ${isMonthly ? "Every Month" : ""}`}
            </DonateButton>

            <FooterText>
              Your donation will benefit <strong>[Donation Benefits]</strong>.
              Donations are tax deductible.
              <br />
              <br />
              <strong>[Donation Benefits]</strong> is a registered 501(c)3
              organization. Federal Tax ID 27-3020119.
            </FooterText>
          </ContentSection>
        </ModalContent>
      </ModalOverlay>
    );
  });
